import { OFREPApi, OFREPApiUnauthorizedError, OFREPForbiddenError, OFREPApiTooManyRequestsError, OFREPApiFetchError, isEvaluationFailureResponse, isEvaluationSuccessResponse } from '@openfeature/ofrep-core';
import { ErrorCode, OpenFeatureEventEmitter, StandardResolutionReasons, ProviderFatalError, ClientProviderEvents, OpenFeatureError, GeneralError } from '@openfeature/web-sdk';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var BulkEvaluationStatus;
(function (BulkEvaluationStatus) {
    BulkEvaluationStatus["SUCCESS_NO_CHANGES"] = "SUCCESS_NO_CHANGES";
    BulkEvaluationStatus["SUCCESS_WITH_CHANGES"] = "SUCCESS_WITH_CHANGES";
})(BulkEvaluationStatus || (BulkEvaluationStatus = {}));

function isResolutionError(response) {
    if (!response || typeof response !== 'object') {
        return false;
    }
    return 'reason' in response && 'errorCode' in response && !('value' in response);
}

const ErrorMessageMap = {
    [ErrorCode.FLAG_NOT_FOUND]: 'Flag was not found',
    [ErrorCode.GENERAL]: 'General error',
    [ErrorCode.INVALID_CONTEXT]: 'Context is invalid or could be parsed',
    [ErrorCode.PARSE_ERROR]: 'Flag or flag configuration could not be parsed',
    [ErrorCode.PROVIDER_FATAL]: 'Provider is in a fatal error state',
    [ErrorCode.PROVIDER_NOT_READY]: 'Provider is not yet ready',
    [ErrorCode.TARGETING_KEY_MISSING]: 'Targeting key is missing',
    [ErrorCode.TYPE_MISMATCH]: 'Flag is not of expected type',
};
class OFREPWebProvider {
    constructor(options, logger) {
        var _a;
        this.DEFAULT_POLL_INTERVAL = 30000;
        this.metadata = {
            name: 'OpenFeature Remote Evaluation Protocol Web Provider',
        };
        this.runsOn = 'client';
        this.events = new OpenFeatureEventEmitter();
        this._flagCache = {};
        this._flagSetMetadataCache = {};
        this._options = options;
        this._logger = logger;
        this._etag = null;
        this._ofrepAPI = new OFREPApi(this._options, this._options.fetchImplementation);
        this._pollingInterval = (_a = this._options.pollInterval) !== null && _a !== void 0 ? _a : this.DEFAULT_POLL_INTERVAL;
    }
    /**
     * Returns a shallow copy of the flag cache, which is updated at initialization/context-change/configuration-change once the flags are re-evaluated.
     */
    get flagCache() {
        return Object.assign({}, this._flagCache);
    }
    /**
     * Initialize the provider, it will evaluate the flags and start the polling if it is not disabled.
     * @param context - the context to use for the evaluation
     */
    initialize(context) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                this._context = context;
                yield this._fetchFlags(context);
                if (this._pollingInterval > 0) {
                    this.startPolling();
                }
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`${this.metadata.name} initialized successfully`);
            }
            catch (error) {
                if (error instanceof OFREPApiUnauthorizedError || error instanceof OFREPForbiddenError) {
                    throw new ProviderFatalError('Initialization failed', { cause: error });
                }
                throw error;
            }
        });
    }
    /* eslint-disable @typescript-eslint/no-unused-vars*/
    /* to make overrides easier we keep these unused vars */
    resolveBooleanEvaluation(flagKey, defaultValue, context) {
        return this._resolve(flagKey, 'boolean', defaultValue);
    }
    resolveStringEvaluation(flagKey, defaultValue, context) {
        return this._resolve(flagKey, 'string', defaultValue);
    }
    resolveNumberEvaluation(flagKey, defaultValue, context) {
        return this._resolve(flagKey, 'number', defaultValue);
    }
    resolveObjectEvaluation(flagKey, defaultValue, context) {
        return this._resolve(flagKey, 'object', defaultValue);
    }
    /**
     * onContextChange is called when the context changes, it will re-evaluate the flags with the new context
     * and update the cache.
     * @param oldContext - the old evaluation context
     * @param newContext - the new evaluation context
     */
    onContextChange(oldContext, newContext) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            try {
                this._context = newContext;
                const now = new Date();
                if (this._retryPollingAfter !== undefined && this._retryPollingAfter > now) {
                    // we do nothing because we should not call the endpoint
                    return;
                }
                yield this._fetchFlags(newContext);
            }
            catch (error) {
                if (error instanceof OFREPApiTooManyRequestsError) {
                    (_a = this.events) === null || _a === void 0 ? void 0 : _a.emit(ClientProviderEvents.Stale, { message: `${error.name}: ${error.message}` });
                    return;
                }
                if (error instanceof OpenFeatureError ||
                    error instanceof OFREPApiFetchError ||
                    error instanceof OFREPApiUnauthorizedError ||
                    error instanceof OFREPForbiddenError) {
                    (_b = this.events) === null || _b === void 0 ? void 0 : _b.emit(ClientProviderEvents.Error, { message: `${error.name}: ${error.message}` });
                    return;
                }
                (_c = this.events) === null || _c === void 0 ? void 0 : _c.emit(ClientProviderEvents.Error, { message: `Unknown error: ${error}` });
            }
        });
    }
    /**
     * onClose is called when the provider is closed, it will stop the polling if it is enabled.
     */
    onClose() {
        this.stopPolling();
        return Promise.resolve();
    }
    /**
     * _fetchFlags is a function that will call the bulk evaluate flags endpoint to get the flags values.
     * @param context - the context to use for the evaluation
     * @private
     * @returns EvaluationStatus if the evaluation the API returned a 304, 200.
     * @throws TargetingKeyMissingError if the API returned a 400 with the error code TargetingKeyMissing
     * @throws InvalidContextError if the API returned a 400 with the error code InvalidContext
     * @throws ParseError if the API returned a 400 with the error code ParseError
     * @throws GeneralError if the API returned a 400 with an unknown error code
     */
    _fetchFlags(context) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const evalReq = {
                    context,
                };
                const response = yield this._ofrepAPI.postBulkEvaluateFlags(evalReq, this._etag);
                if (response.httpStatus === 304) {
                    // nothing has changed since last time, we are doing nothing
                    return { status: BulkEvaluationStatus.SUCCESS_NO_CHANGES, flags: [] };
                }
                if (response.httpStatus !== 200) {
                    throw new GeneralError(`Failed OFREP bulk evaluation request, status: ${response.httpStatus}`);
                }
                const bulkSuccessResp = response.value;
                const newCache = {};
                if ('flags' in bulkSuccessResp && Array.isArray(bulkSuccessResp.flags)) {
                    bulkSuccessResp.flags.forEach((evalResp) => {
                        if (isEvaluationFailureResponse(evalResp)) {
                            newCache[evalResp.key] = {
                                reason: StandardResolutionReasons.ERROR,
                                flagMetadata: evalResp.metadata,
                                errorCode: evalResp.errorCode,
                                errorDetails: evalResp.errorDetails,
                            };
                        }
                        if (isEvaluationSuccessResponse(evalResp) && evalResp.key) {
                            newCache[evalResp.key] = {
                                value: evalResp.value,
                                variant: evalResp.variant,
                                reason: evalResp.reason,
                                flagMetadata: evalResp.metadata,
                            };
                        }
                    });
                    const listUpdatedFlags = this._getListUpdatedFlags(this._flagCache, newCache);
                    this._flagCache = newCache;
                    this._etag = (_a = response.httpResponse) === null || _a === void 0 ? void 0 : _a.headers.get('etag');
                    this._flagSetMetadataCache = typeof bulkSuccessResp.metadata === 'object' ? bulkSuccessResp.metadata : {};
                    return { status: BulkEvaluationStatus.SUCCESS_WITH_CHANGES, flags: listUpdatedFlags };
                }
                else {
                    throw new Error('No flags in OFREP bulk evaluation response');
                }
            }
            catch (error) {
                if (error instanceof OFREPApiTooManyRequestsError && error.retryAfterDate !== null) {
                    this._retryPollingAfter = error.retryAfterDate;
                }
                throw error;
            }
        });
    }
    /**
     * _getListUpdatedFlags is a function that will compare the old cache with the new cache and
     * return the list of flags that have been updated / deleted / created.
     * @param oldCache
     * @param newCache
     * @private
     */
    _getListUpdatedFlags(oldCache, newCache) {
        const changedKeys = [];
        const oldKeys = Object.keys(oldCache);
        const newKeys = Object.keys(newCache);
        // Check for added or modified keys in newCache
        for (const key in newCache) {
            if (oldKeys.indexOf(key) === -1 || JSON.stringify(oldCache[key]) !== JSON.stringify(newCache[key])) {
                changedKeys.push(key);
            }
        }
        // Check for removed keys in oldCache
        for (const key in oldCache) {
            if (newKeys.indexOf(key) === -1) {
                changedKeys.push(key);
            }
        }
        return changedKeys;
    }
    /**
     * _resolve is a function retrieving the value from a flag in the cache.
     * @param flagKey - name of the flag to retrieve
     * @param type - type of the flag
     * @param defaultValue - default value
     * @private
     */
    _resolve(flagKey, type, defaultValue) {
        const resolved = this._flagCache[flagKey];
        if (!resolved) {
            return {
                value: defaultValue,
                flagMetadata: this._flagSetMetadataCache,
                reason: StandardResolutionReasons.ERROR,
                errorCode: ErrorCode.FLAG_NOT_FOUND,
                errorMessage: ErrorMessageMap[ErrorCode.FLAG_NOT_FOUND],
            };
        }
        if (isResolutionError(resolved)) {
            return Object.assign(Object.assign({}, resolved), { value: defaultValue, errorMessage: ErrorMessageMap[resolved.errorCode] });
        }
        if (typeof resolved.value !== type) {
            return {
                value: defaultValue,
                flagMetadata: resolved.flagMetadata,
                reason: StandardResolutionReasons.ERROR,
                errorCode: ErrorCode.TYPE_MISMATCH,
                errorMessage: ErrorMessageMap[ErrorCode.TYPE_MISMATCH],
            };
        }
        return {
            variant: resolved.variant,
            value: resolved.value,
            flagMetadata: resolved.flagMetadata,
            errorCode: resolved.errorCode,
            errorMessage: resolved.errorMessage,
            reason: resolved.reason,
        };
    }
    /**
     * Start polling for flag updates, it will call the bulk update function every pollInterval
     * @private
     */
    startPolling() {
        this._pollingIntervalId = setInterval(() => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                const now = new Date();
                if (this._retryPollingAfter !== undefined && this._retryPollingAfter > now) {
                    return;
                }
                const res = yield this._fetchFlags(this._context);
                if (res.status === BulkEvaluationStatus.SUCCESS_WITH_CHANGES) {
                    (_a = this.events) === null || _a === void 0 ? void 0 : _a.emit(ClientProviderEvents.ConfigurationChanged, {
                        message: 'Flags updated',
                        flagsChanged: res.flags,
                    });
                }
            }
            catch (error) {
                (_b = this.events) === null || _b === void 0 ? void 0 : _b.emit(ClientProviderEvents.Stale, { message: `Error while polling: ${error}` });
            }
        }), this._pollingInterval);
    }
    /**
     * Stop polling for flag updates
     * @private
     */
    stopPolling() {
        if (this._pollingIntervalId) {
            clearInterval(this._pollingIntervalId);
        }
    }
}

export { OFREPWebProvider };
